import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Alert } from "../components/Alert";
import { Spinner } from "../components/Spinner";
import { ApiError, fetchSessions, revokeSession } from "../lib/api";
import type { SessionItem } from "../lib/types";
import { useConfig } from "../state/config-context";

function formatDate(value: string | null, locale: string): string {
  if (value === null || value === "") {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export function SessionsPage(): ReactNode {
  const { t, lang } = useConfig();
  const [items, setItems] = useState<SessionItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSessions();
      setItems(data.items);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const onRevoke = async (id: string): Promise<void> => {
    setBusyId(id);
    setError(null);
    try {
      await revokeSession(id);
      setConfirmId(null);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.detail : t("error.network"));
    } finally {
      setBusyId(null);
    }
  };

  const locale = lang === "es" ? "es-ES" : "en-US";

  return (
    <section className="page" aria-labelledby="sessions-title">
      <header className="page__header">
        <h1 id="sessions-title">{t("sessions.title")}</h1>
        <p className="muted">{t("sessions.subtitle")}</p>
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}

      {loading ? <Spinner label={t("app.loading")} /> : null}

      {!loading && items !== null && items.length === 0 ? (
        <p className="empty">{t("sessions.empty")}</p>
      ) : null}

      {!loading && items !== null && items.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("sessions.title")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("sessions.device")}</th>
                <th scope="col">{t("sessions.app")}</th>
                <th scope="col">{t("sessions.lastSeen")}</th>
                <th scope="col">{t("sessions.created")}</th>
                <th scope="col" className="table__actions-col">
                  <span className="sr-only">{t("sessions.revoke")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    {item.device_id ?? t("sessions.unknown")}
                    {item.current ? <span className="badge">{t("sessions.current")}</span> : null}
                  </td>
                  <td>{item.app ?? "—"}</td>
                  <td>{formatDate(item.last_seen_at, locale)}</td>
                  <td>{formatDate(item.created_at, locale)}</td>
                  <td className="table__actions-col">
                    {item.current ? null : confirmId === item.id ? (
                      <span className="inline-actions">
                        <button
                          type="button"
                          className="btn btn--danger btn--sm"
                          disabled={busyId === item.id}
                          onClick={() => void onRevoke(item.id)}
                        >
                          {busyId === item.id ? t("common.saving") : t("sessions.revoke")}
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => setConfirmId(null)}
                          disabled={busyId === item.id}
                        >
                          {t("common.cancel")}
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setConfirmId(item.id)}
                      >
                        {t("sessions.revoke")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
